import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, useLanguage, useTheme } from "../App";
import { Button } from "../components/ui/button";
import BookingModal from "../components/BookingModal";
import ClientPWAInstall from "../components/ClientPWAInstall";
import {
  Star, MapPin, Phone, Mail, Sun, Moon, Instagram, Facebook,
  Globe, MessageCircle, Sparkles, Calendar as CalendarIcon, Briefcase,
  Clock, Users, Image as ImageIcon, Award, ChevronRight, Plus
} from "lucide-react";

// Sector-specific stock fallback gallery (Unsplash)
const STOCK_GALLERY = {
  beauty:   ["https://images.unsplash.com/photo-1560066984-138dadb4c035?w=900","https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=900","https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=900"],
  parrucchiere: ["https://images.unsplash.com/photo-1560066984-138dadb4c035?w=900","https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=900","https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=900"],
  estetica: ["https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=900","https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=900","https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=900"],
  dental:   ["https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=900","https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=900","https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=900"],
  dental_pro: ["https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=900","https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=900","https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=900"],
  medical:  ["https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=900","https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900","https://images.unsplash.com/photo-1585435557343-3b092031d4f0?w=900"],
  fitness:  ["https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=900","https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=900","https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=900"],
  generic:  ["https://images.unsplash.com/photo-1497366216548-37526070297c?w=900","https://images.unsplash.com/photo-1604328698692-f76ea9498e76?w=900","https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=900"]
};

const BookingPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  const [salon, setSalon] = useState(null);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [closurePeriods, setClosurePeriods] = useState([]);
  const [weeklyClosedDays, setWeeklyClosedDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [preselectedService, setPreselectedService] = useState(null);

  const trackedRef = useRef(false);

  // Tracking helper
  const track = async (eventType, meta = {}) => {
    try {
      await axios.post(`${API}/public/smartpage/track`, {
        event_type: eventType, slug, meta
      });
    } catch (e) { /* fire-and-forget */ }
  };

  // Fetch data
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const r = await axios.get(`${API}/public/salon/${slug}`);
        setSalon(r.data.tenant);
        setServices(r.data.services || []);
        setStaff(r.data.staff || []);
        setReviews(r.data.reviews || []);
        try {
          const c = await axios.get(`${API}/public/salon/${slug}/closures`);
          setClosurePeriods(c.data.closure_periods || []);
          setWeeklyClosedDays(c.data.weekly_closed_days || []);
        } catch (e) {/* ignore */}
      } catch (err) {
        setError(language === "it" ? "Salone non trovato" : "Salon not found");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [slug, language]);

  // Page view (single-fire)
  useEffect(() => {
    if (salon && !trackedRef.current) {
      trackedRef.current = true;
      track("page_view");
    }
  }, [salon]);

  // Update PWA manifest + meta
  useEffect(() => {
    if (!salon) return;
    const updateMeta = async () => {
      try {
        const m = await axios.get(`${API}/public/salon/${slug}/meta`);
        document.title = m.data.title || `${salon.name}`;
        const oldMan = document.querySelector('link[rel="manifest"]'); if (oldMan) oldMan.remove();
        const newMan = document.createElement("link"); newMan.rel = "manifest"; newMan.href = m.data.manifest_url;
        document.head.appendChild(newMan);
        const tc = document.querySelector('meta[name="theme-color"]');
        if (tc) tc.content = m.data.theme_color || "#6366f1";
      } catch (e) {/* fallback */}
    };
    updateMeta();
    return () => { document.title = "SmartBooker"; };
  }, [salon, slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Briefcase className="w-8 h-8 text-white" />
          </div>
          <p className="text-slate-500 dark:text-slate-400">{language === "it" ? "Caricamento..." : "Loading..."}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
        <div className="text-center p-8">
          <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6"><span className="text-4xl">😕</span></div>
          <h1 className="text-2xl font-bold mb-2">{error}</h1>
        </div>
      </div>
    );
  }

  const brandPrimary = salon?.branding?.primary_color || "#6366f1";
  const brandSecondary = salon?.branding?.secondary_color || "#8b5cf6";
  const brandAccent = salon?.branding?.accent_color || "#ec4899";
  const sps = salon?.smartpage_settings || {};
  const showPrices = sps.show_prices !== false;
  const showTeam = sps.show_team !== false;
  const showGallery = sps.show_gallery !== false;
  const showReviews = sps.show_reviews !== false;
  const ctaWhatsapp = sps.cta_whatsapp_enabled !== false;
  const footerLink = sps.footer_link_enabled !== false;

  const social = salon?.social_links || {};
  const sector = salon?.sector || "generic";
  const stockImgs = STOCK_GALLERY[sector] || STOCK_GALLERY.generic;
  const galleryImgs = (salon?.gallery && salon.gallery.length > 0)
    ? salon.gallery
    : stockImgs;
  const isStockGallery = !salon?.gallery || salon.gallery.length === 0;

  const coverImage = salon?.cover_image || stockImgs[0] || "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1600";

  // Reviews stats
  const reviewCount = reviews.length;
  const avgRating = reviewCount > 0
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviewCount).toFixed(1)
    : null;

  // Opening hours
  const dayLabels = language === "it"
    ? { monday: "Lun", tuesday: "Mar", wednesday: "Mer", thursday: "Gio", friday: "Ven", saturday: "Sab", sunday: "Dom" }
    : { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };
  const dayOrder = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
  const todayIndex = (new Date().getDay() + 6) % 7; // Monday=0
  const todayKey = dayOrder[todayIndex];
  const settings = salon?.settings || {};
  const opening = settings.opening_hours || {};

  const formatHours = (h) => {
    if (!h || h.closed) return language === "it" ? "Chiuso" : "Closed";
    if (h.continuous === false) return `${h.morning_start || "09:00"}–${h.morning_end || "13:00"}, ${h.afternoon_start || "15:00"}–${h.afternoon_end || "19:00"}`;
    return `${h.start || "09:00"}–${h.end || "19:00"}`;
  };

  const todayHours = opening[todayKey];
  const isOpenToday = todayHours && !todayHours.closed;

  // CTAs
  const openBookingModal = (service = null) => {
    setPreselectedService(service);
    setModalOpen(true);
    track("clicked_book_now", { service_id: service?.service_id, position: service ? "service_card" : "hero" });
  };

  const cleanPhone = salon?.phone ? salon.phone.replace(/\D/g, "") : "";
  const waNumber = (social.whatsapp || salon?.phone || "").replace(/\D/g, "");
  const mapsQuery = encodeURIComponent(`${salon?.name || ""} ${salon?.address || ""} ${salon?.city || ""}`);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* ============ HERO ============ */}
      <section className="relative" data-testid="smartpage-hero">
        <div className="relative h-[55vh] sm:h-[65vh] min-h-[400px] overflow-hidden">
          <img src={coverImage} alt={salon?.name} className="w-full h-full object-cover scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/80" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 50%, ${brandPrimary}40 100%)` }} />

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="absolute top-4 right-4 p-3 rounded-full bg-white/15 backdrop-blur-md hover:bg-white/25 transition-colors border border-white/20"
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "light" ? <Moon className="w-5 h-5 text-white" /> : <Sun className="w-5 h-5 text-white" />}
          </button>

          {/* Hero content */}
          <div className="absolute inset-0 flex flex-col justify-end pb-8 sm:pb-14 px-5 sm:px-10">
            <div className="max-w-4xl mx-auto w-full">
              <div className="flex items-end gap-4 mb-5">
                {salon?.logo_url ? (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white p-2 shadow-2xl flex-shrink-0">
                    <img src={salon.logo_url} alt={salon.name} className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center shadow-2xl flex-shrink-0" style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}>
                    <span className="text-3xl sm:text-4xl font-bold text-white">{salon?.name?.[0]}</span>
                  </div>
                )}
                {isOpenToday !== undefined && (
                  <div className="mb-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold backdrop-blur-md ${isOpenToday ? "bg-emerald-500/30 text-emerald-50 border border-emerald-300/40" : "bg-red-500/30 text-red-50 border border-red-300/40"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isOpenToday ? "bg-emerald-300 animate-pulse" : "bg-red-300"}`} />
                      {isOpenToday ? (language === "it" ? "Aperto ora" : "Open now") : (language === "it" ? "Chiuso" : "Closed")}
                      {isOpenToday && todayHours && <span className="opacity-80">· {formatHours(todayHours)}</span>}
                    </span>
                  </div>
                )}
              </div>

              {salon?.category_label && (
                <p className="text-white/80 text-xs sm:text-sm font-medium uppercase tracking-widest mb-2">
                  {salon.category_label}
                </p>
              )}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white drop-shadow-2xl mb-3 leading-tight">
                {salon?.name}
              </h1>
              {(salon?.about_short) && (
                <p className="text-white/90 text-base sm:text-lg max-w-2xl mb-5 leading-relaxed drop-shadow-md">
                  {salon.about_short}
                </p>
              )}
              {salon?.address && (
                <p className="text-white/85 flex items-center gap-2 text-sm mb-6">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span>{salon.address}{salon.city ? `, ${salon.city}` : ""}</span>
                </p>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => openBookingModal()}
                  className="rounded-full px-7 py-6 text-base font-semibold shadow-2xl hover:scale-[1.03] hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] transition-all"
                  style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}
                  data-testid="hero-book-now"
                >
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  {language === "it" ? "Prenota ora" : "Book now"}
                </Button>
                {ctaWhatsapp && waNumber && (
                  <a
                    href={`https://wa.me/${waNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track("clicked_whatsapp")}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-green-500 hover:bg-green-600 text-white font-medium transition-all shadow-xl hover:scale-[1.03]"
                    data-testid="hero-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp
                  </a>
                )}
                {salon?.phone && (
                  <a
                    href={`tel:${salon.phone}`}
                    onClick={() => track("clicked_call")}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 text-white font-medium transition-all border border-white/30"
                    data-testid="hero-call"
                  >
                    <Phone className="w-5 h-5" />
                    {language === "it" ? "Chiama" : "Call"}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST BAR ============ */}
      <section className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900" data-testid="smartpage-trust-bar">
        <div className="max-w-4xl mx-auto px-5 sm:px-10 py-5 grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="font-bold text-lg">{avgRating || "5.0"}</span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {reviewCount > 0
                ? `${reviewCount} ${language === "it" ? "recensioni" : "reviews"}`
                : (language === "it" ? "Top rated" : "Top rated")}
            </p>
          </div>
          <div className="text-center border-x border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Briefcase className="w-4 h-4 text-violet-500" />
              <span className="font-bold text-lg">{services.length}</span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{language === "it" ? "Servizi" : "Services"}</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Users className="w-4 h-4 text-emerald-500" />
              <span className="font-bold text-lg">{staff.length || "1"}</span>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{language === "it" ? "Professionisti" : "Pros"}</p>
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      {salon?.about_long && (
        <section className="py-12 sm:py-16 px-5 sm:px-10" data-testid="smartpage-about">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: brandPrimary }} />
              {language === "it" ? "Chi siamo" : "About us"}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-base sm:text-lg whitespace-pre-line">
              {salon.about_long}
            </p>
          </div>
        </section>
      )}

      {/* ============ SERVICES ============ */}
      <section className="py-12 sm:py-16 px-5 sm:px-10 bg-slate-50 dark:bg-slate-900/50" data-testid="smartpage-services">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end justify-between mb-7">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-1">{language === "it" ? "I nostri servizi" : "Our services"}</h2>
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                {language === "it" ? "Clicca per prenotare in 30 secondi" : "Click to book in 30 seconds"}
              </p>
            </div>
          </div>

          {services.length === 0 ? (
            <div className="text-center py-12 text-slate-400">{language === "it" ? "Nessun servizio disponibile" : "No services available"}</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {services.map((service) => (
                <button
                  key={service.service_id}
                  onClick={() => openBookingModal(service)}
                  className="text-left p-5 rounded-2xl border-2 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-violet-400 dark:hover:border-violet-500 hover:shadow-lg transition-all group"
                  data-testid={`smartpage-service-${service.service_id}`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-base group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">{service.name}</h3>
                      {service.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{service.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-3 text-xs">
                        <span className="inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-full text-slate-600 dark:text-slate-300">
                          <Clock className="w-3 h-3" />{service.duration} min
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {showPrices ? (
                        <div className="text-2xl font-bold" style={{ color: brandPrimary }}>€{service.price}</div>
                      ) : (
                        <div className="text-sm text-slate-400 italic">{language === "it" ? "Su richiesta" : "On request"}</div>
                      )}
                      <div className="mt-2 inline-flex items-center text-xs font-medium text-slate-400 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all">
                        {language === "it" ? "Prenota" : "Book"} <ChevronRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============ TEAM ============ */}
      {showTeam && staff.length > 0 && (
        <section className="py-12 sm:py-16 px-5 sm:px-10" data-testid="smartpage-team">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-7 flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: brandPrimary }} />
              {language === "it" ? "Il nostro team" : "Our team"}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {staff.map((s) => (
                <div key={s.staff_id} className="text-center group" data-testid={`team-member-${s.staff_id}`}>
                  {s.avatar_url ? (
                    <img src={s.avatar_url} alt={s.name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mx-auto mb-3 ring-4 ring-white dark:ring-slate-800 shadow-lg group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full mx-auto mb-3 flex items-center justify-center text-white text-3xl font-bold shadow-lg group-hover:scale-105 transition-transform" style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}>
                      {s.name?.[0]}
                    </div>
                  )}
                  <p className="font-semibold text-sm">{s.name}</p>
                  {s.role && <p className="text-xs text-slate-500 dark:text-slate-400">{s.role}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ HOURS ============ */}
      <section className="py-12 sm:py-16 px-5 sm:px-10 bg-slate-50 dark:bg-slate-900/50" data-testid="smartpage-hours">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 flex items-center gap-2">
            <Clock className="w-6 h-6" style={{ color: brandPrimary }} />
            {language === "it" ? "Orari di apertura" : "Opening hours"}
          </h2>
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-slate-200 dark:border-slate-700">
            <div className="space-y-2.5">
              {dayOrder.map((d) => {
                const h = opening[d];
                const isToday = d === todayKey;
                return (
                  <div key={d} className={`flex justify-between items-center py-2 px-3 rounded-xl ${isToday ? "bg-violet-50 dark:bg-violet-900/20 font-semibold" : ""}`}>
                    <span className="text-sm flex items-center gap-2">
                      {dayLabels[d]}
                      {isToday && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium text-white" style={{ background: brandPrimary }}>{language === "it" ? "Oggi" : "Today"}</span>}
                    </span>
                    <span className={`text-sm ${(!h || h.closed) ? "text-red-500" : "text-slate-700 dark:text-slate-300"}`}>
                      {formatHours(h)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ============ GALLERY ============ */}
      {showGallery && (
        <section className="py-12 sm:py-16 px-5 sm:px-10" data-testid="smartpage-gallery">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-end justify-between mb-7">
              <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <ImageIcon className="w-6 h-6" style={{ color: brandPrimary }} />
                {language === "it" ? "Galleria" : "Gallery"}
              </h2>
              {isStockGallery && (
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{language === "it" ? "Anteprima" : "Preview"}</span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {galleryImgs.slice(0, 6).map((img, i) => (
                <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-slate-100 dark:bg-slate-800 group cursor-pointer" data-testid={`gallery-img-${i}`}>
                  <img src={img} alt={`Galleria ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                </div>
              ))}
            </div>
            {isStockGallery && (
              <div className="mt-6 p-4 rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-center">
                <Plus className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {language === "it"
                    ? "Carica le tue foto reali dal pannello tenant per personalizzare la galleria."
                    : "Upload your own photos from the tenant dashboard to customize the gallery."}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============ REVIEWS ============ */}
      {showReviews && reviews.length > 0 && (
        <section className="py-12 sm:py-16 px-5 sm:px-10 bg-slate-50 dark:bg-slate-900/50" data-testid="smartpage-reviews">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-7">
              <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                {language === "it" ? "Recensioni" : "Reviews"}
              </h2>
              {avgRating && (
                <div className="text-right">
                  <div className="text-3xl font-bold">{avgRating}</div>
                  <div className="flex items-center gap-0.5 justify-end">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-3 h-3 ${i < Math.round(avgRating) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {reviews.slice(0, 6).map((r, i) => (
                <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm" data-testid={`review-${i}`}>
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className={`w-4 h-4 ${s < (r.rating || 0) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                    ))}
                  </div>
                  {r.comment && <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed mb-3">"{r.comment}"</p>}
                  <p className="text-xs font-medium text-slate-500">— {r.client_name || (language === "it" ? "Cliente" : "Client")}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ LOCATION ============ */}
      {salon?.address && (
        <section className="py-12 sm:py-16 px-5 sm:px-10" data-testid="smartpage-location">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 flex items-center gap-2">
              <MapPin className="w-6 h-6" style={{ color: brandPrimary }} />
              {language === "it" ? "Dove siamo" : "Where we are"}
            </h2>
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm">
              <p className="font-medium mb-1">{salon.name}</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
                {salon.address}{salon.city ? `, ${salon.city}` : ""}
              </p>
              <div className="aspect-video rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-900 mb-4">
                <iframe
                  title="map"
                  width="100%" height="100%" style={{ border: 0 }}
                  loading="lazy" allowFullScreen
                  src={`https://www.google.com/maps?q=${mapsQuery}&output=embed`}
                />
              </div>
              <a
                href={`https://www.google.com/maps?q=${mapsQuery}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-medium hover:underline"
                style={{ color: brandPrimary }}
                data-testid="open-in-maps"
              >
                {language === "it" ? "Apri in Google Maps" : "Open in Google Maps"}
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ============ CONTACT ============ */}
      <section className="py-12 sm:py-16 px-5 sm:px-10 bg-slate-50 dark:bg-slate-900/50" data-testid="smartpage-contact">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">{language === "it" ? "Contatti" : "Contact"}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {salon?.phone && (
              <a href={`tel:${salon.phone}`} onClick={() => track("clicked_call")} className="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-violet-400 transition-all" data-testid="contact-phone">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${brandPrimary}20, ${brandSecondary}20)` }}>
                  <Phone className="w-5 h-5" style={{ color: brandPrimary }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">{language === "it" ? "Chiama" : "Call"}</p>
                  <p className="font-medium text-sm truncate">{salon.phone}</p>
                </div>
              </a>
            )}
            {salon?.email && (
              <a href={`mailto:${salon.email}`} className="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-violet-400 transition-all" data-testid="contact-email">
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${brandPrimary}20, ${brandSecondary}20)` }}>
                  <Mail className="w-5 h-5" style={{ color: brandPrimary }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium text-sm truncate">{salon.email}</p>
                </div>
              </a>
            )}
            {ctaWhatsapp && waNumber && (
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" onClick={() => track("clicked_whatsapp")} className="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-green-400 transition-all" data-testid="contact-whatsapp">
                <div className="w-11 h-11 rounded-full bg-green-500 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-white" /></div>
                <div className="min-w-0">
                  <p className="text-xs text-slate-500">WhatsApp</p>
                  <p className="font-medium text-sm">{language === "it" ? "Scrivici" : "Message us"}</p>
                </div>
              </a>
            )}
          </div>

          {/* Socials */}
          {(social.instagram || social.facebook || social.website) && (
            <div className="mt-6 flex gap-3 justify-center">
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-instagram"><Instagram className="w-5 h-5 text-white" /></a>
              )}
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-blue-600 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-facebook"><Facebook className="w-5 h-5 text-white" /></a>
              )}
              {social.website && (
                <a href={social.website} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full bg-slate-700 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" data-testid="social-website"><Globe className="w-5 h-5 text-white" /></a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ============ PREMIUM CTA ============ */}
      <section className="py-14 px-5 sm:px-10 relative overflow-hidden" data-testid="smartpage-premium-cta">
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary} 60%, ${brandAccent})` }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
        <div className="relative max-w-3xl mx-auto text-center text-white">
          <Sparkles className="w-10 h-10 mx-auto mb-4 opacity-80" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">{language === "it" ? "Prenota in 30 secondi" : "Book in 30 seconds"}</h2>
          <p className="text-white/90 text-base sm:text-lg mb-7 max-w-xl mx-auto">
            {language === "it"
              ? "Scegli il servizio, l'orario e ricevi conferma immediata. Zero attese."
              : "Pick a service, a time, get instant confirmation. No waiting."}
          </p>
          <Button
            onClick={() => openBookingModal()}
            className="rounded-full px-8 py-7 text-base font-bold bg-white text-slate-900 hover:bg-white/90 shadow-2xl hover:scale-[1.04] transition-all"
            data-testid="premium-cta-book"
          >
            <CalendarIcon className="w-5 h-5 mr-2" />
            {language === "it" ? "Prenota ora" : "Book now"}
          </Button>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-8 px-5 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto text-center text-sm text-slate-400">
          <p>© {new Date().getFullYear()} {salon?.name}. {language === "it" ? "Tutti i diritti riservati." : "All rights reserved."}</p>
          {footerLink && (
            <p className="mt-1">
              {language === "it" ? "Powered by" : "Powered by"}{" "}
              <a href="https://smartbooker.it" target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={{ color: brandPrimary }}>
                SmartBooker
              </a>
            </p>
          )}
        </div>
      </footer>

      {/* ============ STICKY MOBILE CTA ============ */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 p-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 shadow-2xl" data-testid="sticky-mobile-cta">
        <Button
          onClick={() => openBookingModal()}
          className="w-full rounded-full py-6 text-base font-bold shadow-xl"
          style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})` }}
          data-testid="sticky-book-now"
        >
          <CalendarIcon className="w-5 h-5 mr-2" />
          {language === "it" ? "Prenota ora" : "Book now"}
        </Button>
      </div>

      {/* Bottom spacer for sticky CTA */}
      <div className="h-20 sm:hidden" />

      {/* Booking Modal */}
      <BookingModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        salon={salon}
        services={services}
        staff={staff}
        slug={slug}
        brandPrimary={brandPrimary}
        brandSecondary={brandSecondary}
        closurePeriods={closurePeriods}
        weeklyClosedDays={weeklyClosedDays}
        initialService={preselectedService}
        onTrack={track}
      />

      {/* PWA install banner */}
      <ClientPWAInstall slug={slug} salonName={salon?.name || "Salon"} brandColor={brandPrimary} language={language} />
    </div>
  );
};

export default BookingPage;
